import {
  Button,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Tooltip,
} from "@fluentui/react-components";
import {
  ArrowRight16Regular,
  Share16Regular,
  Delete16Regular,
  Edit16Regular,
  Link16Regular,
  MoreVertical20Regular,
  Star16Filled,
  Star16Regular,
} from "@fluentui/react-icons";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "store";
import {
  useSharedCloseButtonStyles,
  useSharedMenuStyles,
} from "theme/sharedStyles";
import { copyTextToClipboard } from "utils/generalUtils";
import { showToast } from "../../../redux/toastActions";
import { useStyles } from "./ChatListItemMenu.styles";
import { IChatListItemMenuProps } from "./ChatListItemMenu.types";
import { ConstantValues } from "common/constants";
import { IWorkspace } from "models/IWorkspace";

export const ChatListItemMenu = ({
  conversation,
  personalWorkspaceId,
  isCurrentUserOwner,
  setIsRenaming,
  setIsMoveDialogOpen,
  setIsCopyOptionsDialogOpen,
  onFavorite,
  onDelete,
  onOpenChange,
}: IChatListItemMenuProps): JSX.Element => {
  const dispatch = useDispatch();
  const workspaces = useSelector((s: RootState) => s.workspacesSlice.value);
  const styles = useStyles();
  const sharedMenuStyles = useSharedMenuStyles();
  const sharedCloseButtonStyles = useSharedCloseButtonStyles();

  if (!conversation) return <></>;

  const conversationWorkspace: IWorkspace | undefined = workspaces.find(
    (w: any) => w.id === conversation.workspaceId,
  );

  const onShare = () => {
    try {
      const shareUrl = `${window.location.origin}?workspaceId=${encodeURIComponent(conversation.workspaceId ?? "")}&conversationId=${encodeURIComponent(conversation.id ?? ConstantValues.EMPTY_STRING)}`;
      copyTextToClipboard(shareUrl);
      dispatch(
        showToast(
          "The link to the chat has been copied to the clipboard.",
          "success",
        ),
      );
    } catch (err) {
      console.error("Failed to copy:", err);
      dispatch(showToast("Failed to copy link", "error"));
    }
  };

  return (
    <Menu
      positioning={{ position: "below", align: "end" }}
      onOpenChange={(_e, data) => onOpenChange?.(data.open)}
    >
      <MenuTrigger>
        <Tooltip
          content="More options"
          relationship="label"
          showDelay={300}
          hideDelay={100}
        >
          <Button
            className={sharedCloseButtonStyles.closeButton}
            appearance="subtle"
            icon={<MoreVertical20Regular />}
            aria-label="More options"
          />
        </Tooltip>
      </MenuTrigger>
      <MenuPopover
        className={styles.menuPopoverPositioned}
        style={{
          visibility: "hidden",
          opacity: 0,
          padding: "0",
          borderRadius: "8px",
          boxShadow:
            "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
        }}
      >
        <MenuList style={{ padding: "0", gap: 0 }}>
          <>
            <MenuItem
              className={sharedMenuStyles.item}
              icon={
                conversation?.isFavorited ? <Star16Filled /> : <Star16Regular />
              }
              onClick={(e) => {
                e.stopPropagation();
                onFavorite();
              }}
            >
              {conversation?.isFavorited ? "Unfavorite" : "Favorite"}
            </MenuItem>
            <MenuItem
              className={sharedMenuStyles.item}
              icon={<ArrowRight16Regular />}
              onClick={(event: React.MouseEvent) => {
                event.stopPropagation();
                setIsMoveDialogOpen(true);
              }}
            >
              Move
            </MenuItem>
            {isCurrentUserOwner &&
              conversationWorkspace &&
              !conversationWorkspace.archived && (
                <MenuItem
                  className={sharedMenuStyles.item}
                  icon={<Edit16Regular />}
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsRenaming(true);
                  }}
                >
                  Rename
                </MenuItem>
              )}
            <MenuItem
              className={sharedMenuStyles.item}
              icon={<Share16Regular />}
              onClick={(e) => {
                e.stopPropagation();
                setIsCopyOptionsDialogOpen(true);
              }}
            >
              Share
            </MenuItem>
          </>
          {isCurrentUserOwner &&
            conversationWorkspace &&
            !conversationWorkspace.archived && (
              <MenuItem
                className={sharedMenuStyles.itemDanger}
                icon={<Delete16Regular />}
                onClick={onDelete}
              >
                Delete
              </MenuItem>
            )}
          {conversation.workspaceId &&
            conversation.workspaceId !== personalWorkspaceId && (
              <MenuItem
                className={sharedMenuStyles.item}
                onClick={onShare}
                icon={<Link16Regular />}
              >
                Copy Link
              </MenuItem>
            )}
        </MenuList>
      </MenuPopover>
    </Menu>
  );
};
